/**
 * El aviso al negocio, oferta por oferta.
 *
 * ── Por qué aquí y no en Stripe ──────────────────────────────────────────────
 * Estuvo en Configuración › Stripe: un solo destinatario para todo el catálogo
 * y sin forma de callar nada. Con la oferta de nueve dólares que se vende
 * veinte veces al día y el programa de dos mil en la misma tienda, eso era o
 * veinte correos al día o ninguno. La decisión es de cada oferta, así que el
 * mando vive en su ficha.
 *
 * ── Los dos mandos ───────────────────────────────────────────────────────────
 * El interruptor se guarda al pulsarlo —es una decisión sola, y una barra de
 * «guardar» para un sí o un no sobra—; los correos destinatarios se gestionan y
 * se guardan con la barra de arriba, como el resto de los campos de texto del panel.
 *
 * Hasta 5 correos pueden recibir el aviso de venta o reserva de cada oferta.
 * Sin ningún correo configurado, los avisos no se envían y el interruptor
 * aparece apagado. Al volver a encenderlo, se introduce una dirección.
 *
 * Apagado esconde el campo en vez de dejarlo en gris: sin avisos no hay
 * destinatario que elegir, y un campo apagado invita a rellenarlo para nada.
 * Lo escrito no se borra —sigue en la base—, así que volver a encenderlo lo
 * devuelve tal cual.
 *
 * ── Qué calla exactamente ────────────────────────────────────────────────────
 * Los correos que llegan AL NEGOCIO por esta oferta: la venta, la baja de su
 * suscripción, la reclamación si es gratuita y el cambio o cancelación de su
 * cita. Nunca los del cliente —confirmación, recordatorio, entrega—, que no son
 * un aviso sino el servicio. Y tampoco el centro de notificaciones del panel,
 * donde cada venta sigue apareciendo.
 */
import { useEffect, useRef, useState } from 'react'
import { IconBell, IconMail, IconPlus, IconUsers, IconUser, IconX } from '@tabler/icons-react'
import { OfferCard } from './OfferCard'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { updateBookingResource, updateOffer, type BookingResource, type Offer } from '../../../lib/api'
import { useToast } from '../../Toast'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { isValidEmailFormat } from '../../../lib/emailCheck'
import { supabase } from '../../../lib/supabase'

function parseEmailList(value?: string | null): string[] {
  if (!value) return []
  return value
    .split(/[;,]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
}

function getInitialAccountEmail(): string {
  if (typeof window === 'undefined') return ''
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const raw = localStorage.getItem(key)
        if (raw) {
          const parsed = JSON.parse(raw) as { user?: { email?: unknown } } | null
          const email = parsed?.user?.email
          if (email && typeof email === 'string') return email.trim().toLowerCase()
        }
      }
    }
  } catch {
    // Si localStorage no está disponible, se resuelve en useEffect
  }
  return ''
}

/** La cara pequeña de una persona del equipo: su foto o sus iniciales. */
function Face({ person }: { person: Pick<BookingResource, 'name' | 'photoUrl'> }) {
  return (
    <span className="dash-booking-artist-face" aria-hidden="true">
      {person.photoUrl ? <img src={person.photoUrl} alt="" /> : person.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()}
    </span>
  )
}

/**
 * El correo de una persona del equipo, editable en el sitio: se guarda al
 * salir del campo. Es el mismo dato que en Equipo.
 */
function PersonEmailRow({ projectId, token, person, onSaved }: {
  projectId: string
  token: string
  person: BookingResource
  onSaved: (email: string) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [draft, setDraft] = useState(person.email ?? '')
  const [saving, setSaving] = useState(false)
  useEffect(() => { setDraft(person.email ?? '') }, [person.email])
  const commit = async () => {
    const value = draft.trim().toLowerCase()
    if (value === (person.email ?? '')) return
    if (value && !isValidEmailFormat(value)) { showToast(t('offers.notify.invalid'), 'error'); setDraft(person.email ?? ''); return }
    setSaving(true)
    try {
      await updateBookingResource(projectId, person.id, { email: value }, token)
      onSaved(value)
    } catch (err) {
      showToast(translateServerError(err, t('error.unexpected')), 'error')
      setDraft(person.email ?? '')
    } finally {
      setSaving(false)
    }
  }
  return (
    <li className={draft.trim() ? '' : 'is-missing'}>
      <Face person={person} />
      <span className="dash-ofd-person-name">{person.name}</span>
      <input
        type="email"
        value={draft}
        placeholder={t('offers.notify.personEmail')}
        aria-label={t('offers.notify.personEmailAria', { name: person.name })}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => { void commit() }}
        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur() }}
      />
    </li>
  )
}

export function OfferSalesNotice({ projectId, token, offer, onChange, bookable = false, team, onTeamChange, allResources = [] }: {
  projectId: string
  token: string
  offer: Offer
  onChange: (offer: Offer) => void
  /**
   * Un servicio con agenda. Cambia la frase, no el ajuste: ahí el aviso llega
   * cuando alguien RESERVA, y hablarle de «ventas» a quien lleva una agenda no
   * describe lo que va a recibir.
   */
  bookable?: boolean
  /**
   * Quiénes dan este servicio, si son varios. Con ellos aparece «Enviar a»:
   * a los correos de la oferta, o a la persona de cada cita.
   */
  team?: BookingResource[] | undefined
  onTeamChange?: ((resources: BookingResource[]) => void) | undefined
  /** El equipo entero, para devolverlo actualizado al guardar un correo. */
  allResources?: BookingResource[]
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)

  const [accountEmail, setAccountEmail] = useState<string>(getInitialAccountEmail)

  const isExplicitlyDisabled = offer.notify?.enabled === false
  const savedPropEmails = parseEmailList(offer.notify?.email)
  const effectiveDefaultEmails = savedPropEmails.length > 0
    ? savedPropEmails
    : (!isExplicitlyDisabled && accountEmail ? [accountEmail] : [])
  const savedEnabled = !isExplicitlyDisabled

  const [emails, setEmails] = useState<string[]>(effectiveDefaultEmails)
  const [draftEmail, setDraftEmail] = useState('')
  const [uiEnabled, setUiEnabled] = useState<boolean>(savedEnabled)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState<'enabled' | 'email' | ''>('')

  /* Confirmar o actualizar el correo de la sesión con Supabase auth */
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      const email = data.session?.user?.email?.trim().toLowerCase()
      if (email) {
        setAccountEmail(email)
        if (!offer.notify?.email && offer.notify?.enabled !== false) {
          setEmails((prev) => (prev.length === 0 ? [email] : prev))
        }
      }
    })
  }, [offer.notify?.email, offer.notify?.enabled])

  /* Lo guardado manda: al volver la oferta del servidor los campos se rehacen */
  useEffect(() => {
    const disabled = offer.notify?.enabled === false
    const parsed = parseEmailList(offer.notify?.email)
    const nextSaved = parsed.length > 0 ? parsed : (!disabled && accountEmail ? [accountEmail] : [])
    setEmails(nextSaved)
    setDraftEmail('')
    setError('')
    setUiEnabled(!disabled)
  }, [offer.notify?.email, offer.notify?.enabled, accountEmail])

  const normalizedCurrent = emails.join(', ')
  const normalizedSaved = effectiveDefaultEmails.join(', ')
  const dirty = normalizedCurrent !== normalizedSaved || draftEmail.trim().length > 0

  const sendTo = team?.length ? (offer.notify?.to ?? 'list') : 'list'
  const save = async (patch: { salesNotifyEnabled?: boolean; salesNotifyEmail?: string; salesNotifyTo?: 'list' | 'person' }, field: 'enabled' | 'email') => {
    setSaving(field)
    try {
      onChange(await updateOffer(projectId, offer.id, patch, token))
      if (field === 'email') showToast(t('offers.notify.saved'))
    } catch (err) {
      showToast(translateServerError(err, t('error.unexpected')), 'error')
    } finally {
      setSaving('')
    }
  }

  const addEmail = (raw: string) => {
    const candidate = raw.trim().toLowerCase()
    if (!candidate) return false
    if (!isValidEmailFormat(candidate)) {
      setError(t('offers.notify.invalid'))
      return false
    }
    if (emails.includes(candidate)) {
      setError(t('offers.notify.duplicate'))
      return false
    }
    if (emails.length >= 5) {
      setError(t('offers.notify.max'))
      return false
    }
    setEmails([...emails, candidate])
    setDraftEmail('')
    setError('')
    return true
  }

  const removeEmail = (emailToRemove: string) => {
    setEmails(emails.filter((item) => item !== emailToRemove))
    setError('')
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      addEmail(draftEmail)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = event.clipboardData.getData('text')
    if (paste.includes(',') || paste.includes(';') || paste.includes('\n')) {
      event.preventDefault()
      const tokens = paste.split(/[,;\n\s]+/).map((item) => item.trim().toLowerCase()).filter(Boolean)
      const next = [...emails]
      for (const tokenItem of tokens) {
        if (next.length >= 5) break
        if (isValidEmailFormat(tokenItem) && !next.includes(tokenItem)) {
          next.push(tokenItem)
        }
      }
      setEmails(next)
      setDraftEmail('')
      setError('')
    }
  }

  const handleToggleSwitch = async () => {
    if (saving !== '') return
    if (uiEnabled) {
      // Apagar avisos
      setUiEnabled(false)
      setError('')
      if (dirty) {
        setDraftEmail('')
      }
      await save({ salesNotifyEnabled: false }, 'enabled')
    } else {
      // Encender avisos
      setUiEnabled(true)
      setError('')
      const nextEmails = emails.length > 0 ? emails : (accountEmail ? [accountEmail] : [])
      if (emails.length === 0 && accountEmail) {
        setEmails([accountEmail])
      }
      if (nextEmails.length > 0) {
        await save({ salesNotifyEnabled: true, salesNotifyEmail: nextEmails.join(', ') }, 'enabled')
      } else {
        setTimeout(() => inputRef.current?.focus(), 50)
      }
    }
  }

  const handleDiscard = () => {
    setEmails(effectiveDefaultEmails)
    setDraftEmail('')
    setError('')
    setUiEnabled(savedEnabled)
  }

  const handleSave = async () => {
    const finalEmails = [...emails]
    const candidate = draftEmail.trim().toLowerCase()
    if (candidate) {
      if (!isValidEmailFormat(candidate)) {
        setError(t('offers.notify.invalid'))
        return
      }
      if (finalEmails.includes(candidate)) {
        setError(t('offers.notify.duplicate'))
        return
      }
      if (finalEmails.length >= 5) {
        setError(t('offers.notify.max'))
        return
      }
      finalEmails.push(candidate)
      setEmails(finalEmails)
      setDraftEmail('')
    }

    if (finalEmails.length === 0) {
      // Sin correos, se apaga y se limpia
      await save({ salesNotifyEnabled: false, salesNotifyEmail: '' }, 'email')
      setUiEnabled(false)
    } else {
      await save({ salesNotifyEnabled: true, salesNotifyEmail: finalEmails.join(', ') }, 'email')
    }
  }

  return (
    <OfferCard
      className="dash-sales-notice"
      icon={<IconBell size={15} />}
      title={t('offers.notify.title')}
      description={t(bookable ? 'offers.notify.bookableBody' : 'offers.notify.body')}
      hint={saving ? t('common.saving') : undefined}
      action={
        <button
          type="button"
          role="switch"
          className={`dash-general-toggle dash-ofd-notice-toggle${uiEnabled ? ' is-on' : ''}`}
          aria-checked={uiEnabled}
          aria-label={t('offers.notify.toggle')}
          disabled={saving !== ''}
          onClick={() => { void handleToggleSwitch() }}
        >
          <span />
        </button>
      }
    >
      <div className="dash-ofd-notice-content">
        {uiEnabled ? (
          <>
            {/* Con equipo: ¿todo a una bandeja, o cada quien las suyas? */}
            {team?.length ? (
              <div className="dash-offer-field dash-ofd-notice-group">
                <span className="dash-offer-field-label">{t('offers.notify.sendTo')}</span>
                <div className="dash-ofd-sendto" role="radiogroup" aria-label={t('offers.notify.sendTo')}>
                  {(['list', 'person'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={sendTo === mode}
                      className={sendTo === mode ? 'is-on' : ''}
                      disabled={saving !== ''}
                      onClick={() => { if (sendTo !== mode) void save({ salesNotifyTo: mode }, 'enabled') }}
                    >
                      {mode === 'list' ? <IconMail size={16} aria-hidden="true" /> : <IconUsers size={16} aria-hidden="true" />}
                      <span>
                        <strong>{t(`offers.notify.to.${mode}`)}</strong>
                        <small>{t(`offers.notify.to.${mode}.hint`)}</small>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {team?.length && sendTo === 'person' ? (
              <div className="dash-offer-field dash-ofd-notice-group">
                <span className="dash-offer-field-label"><IconUser size={13} aria-hidden="true" /> {t('offers.notify.personList')}</span>
                <ul className="dash-ofd-people">
                  {team.map((person) => (
                    <PersonEmailRow
                      key={person.id}
                      projectId={projectId}
                      token={token}
                      person={person}
                      onSaved={(email) => onTeamChange?.(allResources.map((row) => (row.id === person.id ? { ...row, email } : row)))}
                    />
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="dash-offer-field dash-ofd-notice-group">
              <span className="dash-offer-field-label">{t(team?.length && sendTo === 'person' ? 'offers.notify.fallbackLabel' : 'offers.notify.emailLabel')}</span>

              {emails.length > 0 && (
                <div className="dash-ofd-emails-list">
                  {emails.map((emailItem) => (
                    <span key={emailItem} className="dash-ofd-email-chip">
                      <span className="dash-ofd-email-chip-text">{emailItem}</span>
                      <button
                        type="button"
                        className="dash-ofd-email-chip-remove"
                        aria-label={t('offers.notify.remove', { email: emailItem })}
                        onClick={() => removeEmail(emailItem)}
                      >
                        <IconX size={13} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {emails.length < 5 ? (
                <div className="dash-ofd-email-input-row">
                  <input
                    ref={inputRef}
                    type="email"
                    value={draftEmail}
                    placeholder={accountEmail || t('offers.notify.emailPlaceholder')}
                    onChange={(event) => {
                      setDraftEmail(event.target.value)
                      if (error) setError('')
                    }}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                  />
                  <button
                    type="button"
                    className="dash-secondary-btn dash-ofd-email-add-btn"
                    disabled={!draftEmail.trim()}
                    onClick={() => addEmail(draftEmail)}
                  >
                    <IconPlus size={14} />
                    <span>{t('offers.notify.add')}</span>
                  </button>
                </div>
              ) : (
                <p className="dash-ofd-email-max-notice">{t('offers.notify.max')}</p>
              )}

              {error ? (
                <small className="dash-ofd-email-error">{error}</small>
              ) : emails.length === 0 ? (
                <small className="dash-ofd-email-need-notice">{t('offers.notify.needEmail')}</small>
              ) : null}
            </div>

            <PendingChangesActions
              dirty={dirty}
              saving={saving === 'email'}
              onDiscard={handleDiscard}
              onSave={() => { void handleSave() }}
            />
          </>
        ) : (
          <p className="dash-card-caption dash-ofd-notice-off">
            {t(bookable ? 'offers.notify.bookableOff' : 'offers.notify.off')}
          </p>
        )}
      </div>
    </OfferCard>
  )
}
